import React, { useEffect, useState } from "react";

import { Row, Col } from "reactstrap";
import { appInsights } from "../../../../config/appInsights";
import BackButtonComponent from "../../../../NewComponents/BackButton/BackButton.component";
import { currencyFormatter } from "../../../../helpers/stringControllers";
import ToggleTab from "../../../../NewComponents/ToggleTab/ToggleTab.component";
import { useLocation, useRouteMatch } from "react-router-dom";
import { useCustomerAccountDetails } from "../../../../custom-hooks/useIndividualCustomer";
import { Loader } from "../../../../UI/Loaders/Loaders";
import Transactions from "./Transactions";
import { ROLE_TYPES } from "../../../../helpers/roleTypes";
import { useSelector, useDispatch } from "react-redux";
import { showModal } from "../../../../redux/actions/modal";

import styles from "./IndividualCustomerAccountDetails.module.scss";

const IndividualCustomerAccountDetails: React.FC<any> = ({ account }) => {
  let location: any = useLocation();
  let {
    accountNumber,
    walletId,
    accountName,
    customerEmail,
    walletProviderId,
  } = location?.state;

  const match = useRouteMatch();
  const { id } = match?.params as { id: string };

  const [activeTab, setActiveTab] = useState("general");
  const dispatch = useDispatch();
  const loggedInUser = useSelector(
    (state: any) => state?.authReducer?.loggedInUser,
  );

  const hasDownloadPermission =
    loggedInUser?.roles &&
    loggedInUser?.roles?.length > 0 &&
    loggedInUser?.roles?.includes(ROLE_TYPES.GENERATE_CASA_BANK_STATEMENT);

  const handleDownloadStatement = () => {
    dispatch(
      showModal({
        modalIdentifier: "downloadaccountstatement",
        dataRecord: {
          accountId: walletId || "",
          accountNumber: accountNumber || details?.data?.accountNumber || "",
          accountName: accountName || details?.data?.product || "",
          customerEmail: customerEmail || "",
          walletProviderId,
        },
      }),
    );
  };

  const {
    data: details,
    isLoading,
    isError,
  } = useCustomerAccountDetails({
    customerId: Number(id),
    walletId,
  });

  useEffect(() => {
    appInsights.trackPageView({
      name: "IndividualCustomerAccountDetails",
      isLoggedIn: true,
    });
  }, []);

  if (isLoading)
    return (
      <Loader centered={true} text="Loading customer account details..." />
    );

  if (isError)
    return (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt p-y-5">
            <i className="fas fa-bell" /> There was an issue while fetching
            customer account details, Please Reload The Page{" "}
          </h4>
        </div>
      </div>
    );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <h3 className="page-title d-flex align-items-center">
          <BackButtonComponent /> Go back
        </h3>
      </div>
      <br />
      <h4 className="page-subtitle advancly-primary-text">{accountName}</h4>
      <br />

      <Row>
        <Col md={3}>
          <div className="card">
            <div className="card-body">
              <p>Product</p>
              <h4 className="page-subtitle advancly-primary-text">
                {details?.data?.product}
              </h4>
            </div>
          </div>
        </Col>
        <Col md={3}>
          <div className="card">
            <div className="card-body">
              <p>Account Number</p>
              <h4 className="page-subtitle advancly-primary-text">
                {details?.data?.accountNumber}
              </h4>
            </div>
          </div>
        </Col>
        <Col md={3}>
          <div className="card">
            <div className="card-body">
              <p>Available Balance</p>
              <h4 className="page-subtitle advancly-primary-text">
                {currencyFormatter(Number(details?.data?.availableBalance))}
              </h4>
            </div>
          </div>
        </Col>
        <Col md={3}>
          <div className="card">
            <div className="card-body">
              <p>Current Balance</p>
              <h4 className="page-subtitle advancly-primary-text">
                {currencyFormatter(Number(details?.data?.currentBalance))}
              </h4>
            </div>
          </div>
        </Col>
      </Row>
      <br />

      <div className="alignToggleTabItems mb-3">
        <ToggleTab
          text="General"
          id="general"
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <ToggleTab
          text="Transactions"
          id="transactions"
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>
      <br />
      <div>
        {activeTab === "general" && (
          <div className={styles.generalWrapper}>
            {hasDownloadPermission && (
              <div className={styles.downloadButtonContainer}>
                <button
                  className="btn advancly-btn"
                  onClick={handleDownloadStatement}
                >
                  <i className="fas fa-download"></i>
                  &nbsp; Download Statement
                </button>
              </div>
            )}

            <div className={styles.general}>
              <div className={styles.details}>
                <h1>Savings Details</h1>
                <div className={styles.cardInformation}>
                  <div className={styles.cardItem}>
                    <p>Activated On</p>
                    <p className={styles.value}>{details?.data?.activatedOn}</p>
                  </div>
                  <div className={styles.cardItem}>
                    <p>Officer Assigned</p>
                    <p className={styles.value}>
                      {details?.data?.officerAssigned}
                    </p>
                  </div>
                  <div className={styles.cardItem}>
                    <p>Total Interest Earned</p>
                    <p className={styles.value}>
                      {currencyFormatter(
                        Number(details?.data?.totalInterestEarned),
                        "NGN",
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.details}>
                <h1>Account Summary</h1>
                <div className={styles.cardInformation}>
                  <div className={styles.cardItem}>
                    <p>Total Withdrawals</p>
                    <p className={styles.value}>
                      {currencyFormatter(
                        Number(details?.data?.totalWithdrawals),
                        "NGN",
                      )}
                    </p>
                  </div>
                  <div className={styles.cardItem}>
                    <p>Interest Earned</p>
                    <p className={styles.value}>
                      {currencyFormatter(
                        Number(details?.data?.interestEarned),
                        "NGN",
                      )}
                    </p>
                  </div>
                  <div className={styles.cardItem}>
                    <p>Nominal Interest Rate</p>
                    <p className={styles.value}>
                      {details?.data?.nominalInterestRate}%
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.details}>
                <h1>Performance History</h1>
                <div className={styles.cardInformation}>
                  <div className={styles.cardItem}>
                    <p>Total Deposits</p>
                    <p className={styles.value}>
                      {currencyFormatter(
                        Number(details?.data?.totalDeposits),
                        "NGN",
                      )}
                    </p>
                  </div>
                  <div className={styles.cardItem}>
                    <p>Total Interest Earned</p>
                    <p className={styles.value}>
                      {currencyFormatter(
                        Number(details?.data?.totalInterestEarned),
                        "NGN",
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "transactions" && <Transactions />}
      </div>
    </div>
  );
};

export default IndividualCustomerAccountDetails;
